import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, FileDown, Images, Trophy, MessageCircle } from "lucide-react";
import { api, fileUrl, waLink } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { SectionTitle } from "@/components/site/PageHero";

const STATS = [
  ["Semen y embriones", "Del Gran Campeón Mundial 2023, Agucho y otros toros y donadoras elite"],
  ["100%", "Criados a campo"],
  ["Costa Rica", "Genética argentina para el trópico"],
  ["Envíos", "A todo el país y el exterior"],
];

function Hero({ hero, poster, settings }) {
  return (
    <section data-testid="home-hero" className="relative h-[100svh] min-h-[600px] overflow-hidden bg-[#142419] grain">
      {poster && <img src={fileUrl(poster.url)} alt="" className="absolute inset-0 h-full w-full object-cover" />}
      {hero?.type === "video" ? (
        <video src={fileUrl(hero.url)} poster={fileUrl(poster?.url)} autoPlay muted loop playsInline className="absolute inset-0 h-full w-full object-cover" data-testid="hero-video" />
      ) : hero ? (
        <img src={fileUrl(hero.url)} alt="" className="absolute inset-0 h-full w-full object-cover" data-testid="hero-image" />
      ) : null}
      <div className="absolute inset-0 bg-gradient-to-r from-[#142419]/85 via-[#142419]/45 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#142419]/80 via-transparent to-transparent" />
      {hero?.type === "video" && hero.title && (
        <p data-testid="hero-video-caption" className="absolute bottom-6 right-24 z-10 hidden md:block max-w-md text-right text-xs uppercase tracking-[0.2em] text-stone-200/90">
          <span className="text-[#C5A059]">En el video - </span>{hero.title}
        </p>
      )}
      <div className="container-fv relative flex h-full flex-col justify-end pb-24 sm:pb-28 text-stone-100">
        <p className="eyebrow !text-[#C5A059] rise">Brangus · Costa Rica</p>
        <h1 data-testid="hero-title" className="f-display mt-5 max-w-3xl text-4xl sm:text-5xl lg:text-6xl leading-[1.04] rise d1">
          Genética de campeones, <em className="text-[#C5A059]">para el trópico.</em>
        </h1>
        <p className="mt-6 max-w-lg text-base sm:text-lg text-stone-200/90 rise d2">
          En Hacienda Fernández &amp; Vargas seleccionamos por fertilidad, desempeño de peso, rusticidad, conformación, calidad de carne en animales con mayor acebuzamiento, generación tras generación.
        </p>
        <div className="mt-9 flex flex-wrap gap-3 rise d3">
          <Link to="/catalogo" data-testid="hero-catalog-cta" className="btn-fv btn-red"><FileDown size={18} />Ver catálogo</Link>
          <a href={waLink(settings.whatsapp, "Hola, quisiera consultar sobre su genética Brangus.")} target="_blank" rel="noreferrer"
            data-testid="hero-whatsapp-cta" className="btn-fv btn-ghost"><MessageCircle size={18} />Consultar por WhatsApp</a>
        </div>
      </div>
    </section>
  );
}

const Stats = () => (
  <section className="bg-[#8B2B22] text-stone-100" data-testid="home-stats">
    <div className="container-fv grid grid-cols-2 lg:grid-cols-4">
      {STATS.map(([a, b], i) => (
        <div key={a} className={`py-8 px-2 sm:px-6 ${i ? "lg:border-l border-white/15" : ""}`}>
          <p className="f-display text-xl sm:text-2xl">{a}</p>
          <p className="mt-1 text-xs uppercase tracking-[0.18em] text-stone-200/80">{b}</p>
        </div>
      ))}
    </div>
  </section>
);

function Featured({ images }) {
  const agucho = images.find((m) => /Agucho/.test(m.title));
  const rest = images.filter((m) => m !== agucho);
  const cards = [
    { to: "/catalogo", icon: FileDown, title: "Catálogo de genética", text: "Descargue el catálogo vigente con embriones, semen y animales disponibles.", img: agucho || rest[1], id: "catalogo" },
    { to: "/galeria", icon: Images, title: "Galería", text: "Algunos de los animales usados en nuestra genética, exposiciones y convenios.", img: rest[0], id: "galeria" },
    { to: "/premios", icon: Trophy, title: "Premios y logros", text: "Tres Cruces Evangelina Pirayú 9285 \"Patricia\", Gran Campeona Hembra Expo Virasoro 2025.", img: { url: PREMIOS_COVER }, id: "premios" },
  ];
  return (
    <section className="container-fv py-20 sm:py-28">
      <SectionTitle eyebrow="Conozca Hacienda Fernández & Vargas" title="Todo lo que necesita antes de elegir su genética" className="max-w-2xl" />
      <div className="mt-14 grid gap-6 md:grid-cols-3">
        {cards.map(({ to, icon: Icon, title, text, img, id }, i) => (
          <Link key={to} to={to} data-testid={`home-featured-${id}`} className={`card-fv img-zoom group overflow-hidden ${i === 1 ? "md:translate-y-10" : ""}`}>
            <div className="aspect-[4/3] overflow-hidden bg-[#1D3324]">
              {img && <img src={fileUrl(img.url)} alt="" className="h-full w-full object-cover" />}
            </div>
            <div className="p-7">
              <Icon className="text-[#8B2B22]" size={22} />
              <h3 className="f-display mt-4 text-2xl">{title}</h3>
              <p className="mt-2 text-sm text-[#5C635E] leading-relaxed">{text}</p>
              <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#8B2B22] transition-[gap] group-hover:gap-3">Ver más <ArrowRight size={16} /></span>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

function ChampionSire({ item, settings }) {
  if (!item) return null;
  return (
    <section className="container-fv pb-20 sm:pb-28 grid gap-10 lg:grid-cols-12 items-center" data-testid="home-champion-sire">
      <div className="lg:col-span-7 overflow-hidden img-zoom">
        <img src={fileUrl(item.url)} alt="Gran Campeón Macho Congreso Mundial Brangus 2023" className="w-full aspect-[3/2] object-cover" />
      </div>
      <div className="lg:col-span-5">
        <p className="eyebrow">Genética disponible</p>
        <h2 className="f-display mt-3 text-3xl sm:text-4xl leading-tight">Gran Campeón Macho<br />Congreso Mundial Brangus 2023</h2>
        <div className="gold-rule mt-5 w-32" />
        <p className="mt-6 text-[#5C635E] leading-relaxed" data-testid="home-champion-text">
          Este toro se encuentra en <strong className="text-[#1A1E1B]">Argentina</strong>. Contamos con <strong className="text-[#1A1E1B]">semen y embriones con él como padre</strong>, así como de <strong className="text-[#1A1E1B]">Agucho</strong> y otros toros y donadoras elite importantes de la raza.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/catalogo" data-testid="home-champion-catalog" className="btn-fv btn-red"><FileDown size={18} />Ver catálogo de genética</Link>
          <a href={waLink(settings.whatsapp, "Hola, quisiera consultar por semen y embriones del Gran Campeón Macho del Congreso Mundial Brangus 2023.")}
            target="_blank" rel="noreferrer" data-testid="home-champion-whatsapp" className="btn-fv btn-wa"><MessageCircle size={18} />Consultar</a>
        </div>
      </div>
    </section>
  );
}

const PREMIOS_COVER = "https://customer-assets-agu9un31.emergentagent.net/job_borrador-creativo/artifacts/c177571d2383a0f1_7e62c84f-158b-4dda-8a79-e61d63e75da0.JPG";

export default function Home() {
  const { settings } = useApp();
  const [items, setItems] = useState([]);
  const [all, setAll] = useState([]);
  useEffect(() => {
    api.get("/media", { params: { featured: true } }).then((r) => setItems(r.data));
    api.get("/media").then((r) => setAll(r.data));
  }, []);
  const hero = items.find((m) => m.type === "video") || items[0] || all[0];
  const images = all.filter((m) => m.type === "image");

  return (
    <div data-testid="home-page">
      <Hero hero={hero} poster={hero?.type === "video" ? items.find((m) => m.type === "image") || images[0] : null} settings={settings} />
      <Stats />
      <Featured images={images} />
      <ChampionSire item={all.find((m) => /Gran Campe[oó]n Macho/i.test(m.title))} settings={settings} />
      <section className="container-fv py-20 sm:py-28 grid gap-10 lg:grid-cols-12 items-center">
        <div className="lg:col-span-7">
          <SectionTitle eyebrow="Consultas" title="¿Busca iniciar un programa de mejoramiento genético con Brangus a través de un proyecto con embriones en convenio?" />
          <p className="mt-6 max-w-xl text-[#5C635E] leading-relaxed">Escríbanos y evaluamos su requerimiento. Atendemos consultas de todo el país y del exterior.</p>
        </div>
        <div className="lg:col-span-5 flex flex-wrap gap-3 lg:justify-end">
          <Link to="/contacto" data-testid="home-contact-cta" className="btn-fv btn-green">Ir a contacto <ArrowRight size={16} /></Link>
          <a href={waLink(settings.whatsapp, "Hola, quisiera hacer una consulta.")} target="_blank" rel="noreferrer" data-testid="home-whatsapp-cta" className="btn-fv btn-wa"><MessageCircle size={18} />WhatsApp</a>
        </div>
      </section>
    </div>
  );
}
